import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { 
  GlassWater, Plus, Search, Edit, Trash2, Save, ArrowRightLeft, 
  ClipboardCheck, AlertTriangle, TrendingDown, BarChart3, Calendar,
  Package, CheckCircle, XCircle, AlertCircle, FileText, Play
} from 'lucide-react';
import { getErrorMessage } from '../lib/utils';

import { API } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';


const getVarianceBadge = (classification) => {
  const config = {
    green: { bg: 'bg-green-100', text: 'text-green-800', label: 'OK' },
    yellow: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Warning' },
    red: { bg: 'bg-red-100', text: 'text-red-800', label: 'Critical' }
  };
  const c = config[classification] || config.green;
  return <Badge className={`${c.bg} ${c.text}`}>{c.label}</Badge>;
};

const getCategoryIcon = (category) => {
  return <GlassWater className="w-4 h-4" />;
};

export default function CrockeryAudit({ user }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Data
  const [constants, setConstants] = useState({ stores: [], categories: [], departments: [], movement_types: [], explanation_options: [] });
  const [crockeryItems, setCrockeryItems] = useState([]);
  const [balances, setBalances] = useState([]);
  const [movements, setMovements] = useState([]);
  const [physicalCounts, setPhysicalCounts] = useState([]);
  const [lossLedger, setLossLedger] = useState([]);
  const [dashboard, setDashboard] = useState({});
  const [countStatus, setCountStatus] = useState({ is_tuesday: false, can_enter: false });
  
  // Filters
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterStore, setFilterStore] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialogs
  const [showAddItem, setShowAddItem] = useState(false);
  const [showMovement, setShowMovement] = useState(false);
  const [showPhysicalCount, setShowPhysicalCount] = useState(false);
  const [showExplain, setShowExplain] = useState(false);
  const [selectedVariance, setSelectedVariance] = useState(null);
  const [showOpeningBalance, setShowOpeningBalance] = useState(false);
  
  // Forms
  const [newItem, setNewItem] = useState({
    item_name: '', category: '', sub_category: '', brand: '', material: '',
    size: '', replacement_cost: '', expected_life_cycles: '', department: ''
  });
  const [movementForm, setMovementForm] = useState({
    crockery_id: '', from_store: '', to_store: '', quantity: '', movement_type: '', remarks: ''
  });
  const [physicalCountForm, setPhysicalCountForm] = useState({ store: '', counts: [] });
  const [explanationForm, setExplanationForm] = useState({ explanation: '', remarks: '' });
  const [openingForm, setOpeningForm] = useState({ crockery_id: '', store: '', quantity: '' });

  const isAdmin = user?.role === 'admin';
  const isStorekeeper = user?.role === 'storekeeper';
  const canManage = isAdmin || isStorekeeper;

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'master') fetchCrockeryItems();
    if (activeTab === 'balances') fetchBalances();
    if (activeTab === 'movements') fetchMovements();
    if (activeTab === 'physical-count') { fetchPhysicalCountStatus(); fetchPhysicalCounts(); }
    if (activeTab === 'loss-ledger') fetchLossLedger();
    if (activeTab === 'dashboard') fetchDashboard();
  }, [activeTab, filterCategory, filterDepartment, filterStore]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const [constantsRes, dashboardRes] = await Promise.all([
        axios.get(`${API}/crockery/constants`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/crockery/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setConstants(constantsRes.data);
      setDashboard(dashboardRes.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load data');
      setLoading(false);
    }
  };

  const fetchCrockeryItems = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (filterCategory !== 'all') params.category = filterCategory;
      if (filterDepartment !== 'all') params.department = filterDepartment;
      if (searchTerm) params.search = searchTerm;
      const res = await axios.get(`${API}/crockery/master`, { headers: { Authorization: `Bearer ${token}` }, params });
      setCrockeryItems(res.data);
    } catch (error) {
      console.error('Failed to fetch items:', error);
    }
  };

  const fetchBalances = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (filterStore !== 'all') params.store = filterStore;
      const res = await axios.get(`${API}/crockery/balances`, { headers: { Authorization: `Bearer ${token}` }, params });
      setBalances(res.data);
    } catch (error) {
      console.error('Failed to fetch balances:', error);
    }
  };

  const fetchMovements = async () => {
    try {
      const token = localStorage.getItem('token');
      const today = new Date().toISOString().slice(0, 10);
      const res = await axios.get(`${API}/crockery/movements`, { 
        headers: { Authorization: `Bearer ${token}` },
        params: { start_date: today }
      });
      setMovements(res.data);
    } catch (error) {
      console.error('Failed to fetch movements:', error);
    }
  };

  const fetchPhysicalCountStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/crockery/physical-count/status`, { headers: { Authorization: `Bearer ${token}` } });
      setCountStatus(res.data);
    } catch (error) {
      console.error('Failed to fetch status:', error);
    }
  };

  const fetchPhysicalCounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (filterStore !== 'all') params.store = filterStore;
      const res = await axios.get(`${API}/crockery/physical-count`, { headers: { Authorization: `Bearer ${token}` }, params });
      setPhysicalCounts(res.data);
    } catch (error) {
      console.error('Failed to fetch counts:', error);
    }
  };

  const fetchLossLedger = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (filterStore !== 'all') params.store = filterStore;
      const res = await axios.get(`${API}/crockery/loss-ledger`, { headers: { Authorization: `Bearer ${token}` }, params });
      setLossLedger(res.data);
    } catch (error) {
      console.error('Failed to fetch loss ledger:', error);
    }
  };

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/crockery/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
      setDashboard(res.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  };

  const handleCreateItem = async () => {
    if (!newItem.item_name || !newItem.category || !newItem.department || !newItem.replacement_cost) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/crockery/master`, {
        ...newItem,
        replacement_cost: parseFloat(newItem.replacement_cost),
        expected_life_cycles: parseInt(newItem.expected_life_cycles) || 0
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Crockery item created');
      setShowAddItem(false);
      setNewItem({ item_name: '', category: '', sub_category: '', brand: '', material: '', size: '', replacement_cost: '', expected_life_cycles: '', department: '' });
      fetchCrockeryItems();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleCreateMovement = async () => {
    if (!movementForm.crockery_id || !movementForm.from_store || !movementForm.to_store || !movementForm.quantity || !movementForm.movement_type) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/crockery/movements`, {
        ...movementForm,
        quantity: parseInt(movementForm.quantity)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Movement recorded');
      setShowMovement(false);
      setMovementForm({ crockery_id: '', from_store: '', to_store: '', quantity: '', movement_type: '', remarks: '' });
      fetchMovements();
      fetchBalances();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleSetOpeningBalance = async () => {
    if (!openingForm.crockery_id || !openingForm.store || !openingForm.quantity) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/crockery/balances/opening`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          crockery_id: openingForm.crockery_id,
          store: openingForm.store,
          quantity: parseInt(openingForm.quantity)
        }
      });
      toast.success('Opening balance set');
      setShowOpeningBalance(false);
      setOpeningForm({ crockery_id: '', store: '', quantity: '' });
      fetchBalances();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleSubmitPhysicalCount = async (crockeryId, physicalQty) => {
    if (!physicalCountForm.store) {
      toast.error('Please select a store first');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/crockery/physical-count`, {
        store: physicalCountForm.store,
        crockery_id: crockeryId,
        physical_qty: parseInt(physicalQty)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Physical count submitted');
      fetchPhysicalCounts();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleExplainVariance = async () => {
    if (!explanationForm.explanation) {
      toast.error('Please select an explanation');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/crockery/loss-ledger/explain`, {
        variance_id: selectedVariance.id,
        explanation: explanationForm.explanation,
        remarks: explanationForm.remarks
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Explanation submitted');
      setShowExplain(false);
      setSelectedVariance(null);
      setExplanationForm({ explanation: '', remarks: '' });
      fetchLossLedger();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleRunAudit = async () => {
    if (!window.confirm('Run weekly audit now? This will calculate variances and reset balances.')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/crockery/audit/run`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Audit completed: ${res.data.variances_processed} items processed, Total loss: ${formatCurrency(res.data.total_loss_value)}`);
      fetchDashboard();
      fetchLossLedger();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <GlassWater className="w-6 h-6 text-blue-600" />
            Crockery Audit System
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Weekly loss audit & inventory tracking
          </p>
        </div>
        <div className="flex items-center gap-2">
          {countStatus.is_tuesday && (
            <Badge className="bg-green-100 text-green-800 animate-pulse">
              <Calendar className="w-3 h-3 mr-1" />
              Tuesday - Physical Count Open
            </Badge>
          )}
          {isAdmin && (
            <Button onClick={handleRunAudit} variant="destructive" data-testid="run-audit-btn">
              <Play className="w-4 h-4 mr-2" />
              Run Audit
            </Button>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <BarChart3 className="w-4 h-4 mr-2" />Dashboard
          </TabsTrigger>
          <TabsTrigger value="master" data-testid="tab-master">
            <Package className="w-4 h-4 mr-2" />Master
          </TabsTrigger>
          <TabsTrigger value="balances" data-testid="tab-balances">
            <GlassWater className="w-4 h-4 mr-2" />Balances
          </TabsTrigger>
          <TabsTrigger value="movements" data-testid="tab-movements">
            <ArrowRightLeft className="w-4 h-4 mr-2" />Movements
          </TabsTrigger>
          <TabsTrigger value="physical-count" data-testid="tab-physical">
            <ClipboardCheck className="w-4 h-4 mr-2" />Physical Count
          </TabsTrigger>
          <TabsTrigger value="loss-ledger" data-testid="tab-loss">
            <TrendingDown className="w-4 h-4 mr-2" />Loss Ledger
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Items</p>
                    <p className="text-2xl font-bold">{dashboard.summary?.total_items || 0}</p>
                  </div>
                  <Package className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Inventory Value</p>
                    <p className="text-2xl font-bold">{formatCurrency(dashboard.summary?.total_inventory_value)}</p>
                  </div>
                  <GlassWater className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card className={dashboard.unexplained_red_variances > 0 ? 'border-red-300 bg-red-50' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Unexplained Variances</p>
                    <p className="text-2xl font-bold text-red-600">{dashboard.unexplained_red_variances || 0}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Physical Counts</p>
                    <p className="text-2xl font-bold">{dashboard.physical_count_status?.counts_entered || 0}</p>
                  </div>
                  <ClipboardCheck className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Weekly Loss Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Weekly Loss Trend</CardTitle>
            </CardHeader>
            <CardContent>
              {dashboard.weekly_losses?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.weekly_losses.map((week) => (
                    <div key={week._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{week._id}</p>
                        <div className="flex gap-2 mt-1">
                          <Badge className="bg-red-100 text-red-800">{week.red_count} Red</Badge>
                          <Badge className="bg-yellow-100 text-yellow-800">{week.yellow_count} Yellow</Badge>
                          <Badge className="bg-green-100 text-green-800">{week.green_count} Green</Badge>
                        </div>
                      </div>
                      <p className="text-xl font-bold text-red-600">{formatCurrency(week.total_loss)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-4">No audit data yet</p>
              )}
            </CardContent>
          </Card>

          {/* Top Lost Items & Loss by Store */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top 10 Lost Items</CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard.top_lost_items?.length > 0 ? (
                  <div className="space-y-2">
                    {dashboard.top_lost_items.map((item, idx) => (
                      <div key={item.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">{idx + 1}.</span>
                          <span className="font-medium">{item.item_name}</span>
                        </div>
                        <span className="text-red-600 font-medium">{formatCurrency(item.loss_value)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-4">No loss data</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Loss by Store</CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard.loss_by_store?.length > 0 ? (
                  <div className="space-y-2">
                    {dashboard.loss_by_store.map((store) => (
                      <div key={store._id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="font-medium">{store._id}</span>
                        <div className="text-right">
                          <p className="text-red-600 font-medium">{formatCurrency(store.total_loss)}</p>
                          <p className="text-xs text-gray-500">{store.variance_count} items</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-4">No store data</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Master Tab */}
        <TabsContent value="master" className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <Label>Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search items..."
                      value={searchTerm}
                      onChange={(e) => { setSearchTerm(e.target.value); fetchCrockeryItems(); }}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="w-[150px]">
                  <Label>Category</Label>
                  <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {constants.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-[150px]">
                  <Label>Department</Label>
                  <Select value={filterDepartment} onValueChange={setFilterDepartment}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Depts</SelectItem>
                      {constants.departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {canManage && (
                  <Button onClick={() => setShowAddItem(true)} data-testid="add-crockery-btn">
                    <Plus className="w-4 h-4 mr-2" />Add Item
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Crockery Master ({crockeryItems.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Sub-Category</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead className="text-right">Cost (₹)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {crockeryItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.item_name}</TableCell>
                      <TableCell>{item.category}</TableCell>
                      <TableCell>{item.sub_category}</TableCell>
                      <TableCell>{item.brand || '-'}</TableCell>
                      <TableCell><Badge variant="secondary">{item.department}</Badge></TableCell>
                      <TableCell className="text-right">{formatCurrency(item.replacement_cost)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Balances Tab */}
        <TabsContent value="balances" className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex gap-4 items-end">
                <div className="w-[200px]">
                  <Label>Store</Label>
                  <Select value={filterStore} onValueChange={setFilterStore}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Stores</SelectItem>
                      {constants.stores.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {canManage && (
                  <Button onClick={() => setShowOpeningBalance(true)} variant="outline">
                    <Plus className="w-4 h-4 mr-2" />Set Opening Balance
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Store Balances</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Store</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {balances.map((bal, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{bal.item_name}</TableCell>
                      <TableCell>{bal.category}</TableCell>
                      <TableCell><Badge variant="outline">{bal.store}</Badge></TableCell>
                      <TableCell className="text-right">{bal.system_qty}</TableCell>
                      <TableCell className="text-right">{formatCurrency(bal.system_qty * (bal.replacement_cost || 0))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Movements Tab */}
        <TabsContent value="movements" className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex justify-between items-center">
                <h3 className="font-medium">Today's Movements</h3>
                {canManage && (
                  <Button onClick={() => setShowMovement(true)} data-testid="add-movement-btn">
                    <Plus className="w-4 h-4 mr-2" />Record Movement
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead>By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movements.map((mov) => (
                    <TableRow key={mov.id}>
                      <TableCell>{mov.date}</TableCell>
                      <TableCell className="font-medium">{mov.item_name}</TableCell>
                      <TableCell>{mov.from_store}</TableCell>
                      <TableCell>{mov.to_store}</TableCell>
                      <TableCell><Badge variant="secondary">{mov.movement_type}</Badge></TableCell>
                      <TableCell className="text-right">{mov.quantity}</TableCell>
                      <TableCell className="text-sm text-gray-500">{mov.entered_by_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Physical Count Tab */}
        <TabsContent value="physical-count" className="space-y-4">
          <Card className={countStatus.is_tuesday ? 'border-green-300 bg-green-50' : 'border-orange-300 bg-orange-50'}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {countStatus.is_tuesday ? (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-orange-600" />
                  )}
                  <div>
                    <p className="font-medium">{countStatus.message}</p>
                    <p className="text-sm text-gray-500">Audit Date: {countStatus.current_tuesday}</p>
                  </div>
                </div>
                {countStatus.can_enter && canManage && (
                  <Select value={physicalCountForm.store} onValueChange={(v) => setPhysicalCountForm({ ...physicalCountForm, store: v })}>
                    <SelectTrigger className="w-[200px]"><SelectValue placeholder="Select Store" /></SelectTrigger>
                    <SelectContent>
                      {constants.stores.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </CardContent>
          </Card>

          {physicalCountForm.store && countStatus.can_enter && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Physical Count for {physicalCountForm.store}</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">System Qty</TableHead>
                      <TableHead className="text-right">Physical Qty</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {balances.filter(b => b.store === physicalCountForm.store).map((bal) => {
                      const existing = physicalCounts.find(c => c.crockery_id === bal.crockery_id && c.store === physicalCountForm.store);
                      return (
                        <TableRow key={bal.crockery_id}>
                          <TableCell className="font-medium">{bal.item_name}</TableCell>
                          <TableCell>{bal.category}</TableCell>
                          <TableCell className="text-right">{bal.system_qty}</TableCell>
                          <TableCell className="w-[120px]">
                            <Input
                              type="number"
                              defaultValue={existing?.physical_qty || ''}
                              placeholder="Count"
                              className="text-right"
                              onBlur={(e) => {
                                if (e.target.value) {
                                  handleSubmitPhysicalCount(bal.crockery_id, e.target.value);
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {existing && <CheckCircle className="w-4 h-4 text-green-600" />}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Loss Ledger Tab */}
        <TabsContent value="loss-ledger" className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex gap-4 items-end">
                <div className="w-[200px]">
                  <Label>Store</Label>
                  <Select value={filterStore} onValueChange={setFilterStore}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Stores</SelectItem>
                      {constants.stores.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingDown className="w-5 h-5" />
                Variance Report
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Store</TableHead>
                    <TableHead className="text-right">System</TableHead>
                    <TableHead className="text-right">Physical</TableHead>
                    <TableHead className="text-right">Variance</TableHead>
                    <TableHead className="text-right">Loss (₹)</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Explanation</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lossLedger.map((entry) => (
                    <TableRow key={entry.id} className={entry.classification === 'red' && !entry.explanation ? 'bg-red-50' : ''}>
                      <TableCell>{entry.audit_date}</TableCell>
                      <TableCell className="font-medium">{entry.item_name}</TableCell>
                      <TableCell>{entry.store}</TableCell>
                      <TableCell className="text-right">{entry.system_qty}</TableCell>
                      <TableCell className="text-right">{entry.physical_qty}</TableCell>
                      <TableCell className={`text-right font-medium ${entry.variance < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {entry.variance}
                      </TableCell>
                      <TableCell className="text-right text-red-600">{formatCurrency(entry.loss_value)}</TableCell>
                      <TableCell>{getVarianceBadge(entry.classification)}</TableCell>
                      <TableCell>
                        {entry.explanation ? (
                          <Badge variant="outline">{entry.explanation}</Badge>
                        ) : entry.classification === 'red' ? (
                          <span className="text-red-600 text-sm">Required</span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {entry.classification === 'red' && !entry.explanation && canManage && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setSelectedVariance(entry); setShowExplain(true); }}
                          >
                            Explain
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Item Dialog */}
      <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Crockery Item</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Item Name *</Label>
              <Input value={newItem.item_name} onChange={(e) => setNewItem({ ...newItem, item_name: e.target.value })} placeholder="e.g., Beer Mug Large" />
            </div>
            <div>
              <Label>Category *</Label>
              <Select value={newItem.category} onValueChange={(v) => setNewItem({ ...newItem, category: v })}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {constants.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sub-Category</Label>
              <Input value={newItem.sub_category} onChange={(e) => setNewItem({ ...newItem, sub_category: e.target.value })} placeholder="e.g., Pint Glass" />
            </div>
            <div>
              <Label>Brand</Label>
              <Input value={newItem.brand} onChange={(e) => setNewItem({ ...newItem, brand: e.target.value })} />
            </div>
            <div>
              <Label>Material</Label>
              <Input value={newItem.material} onChange={(e) => setNewItem({ ...newItem, material: e.target.value })} placeholder="Glass, Ceramic" />
            </div>
            <div>
              <Label>Size</Label>
              <Input value={newItem.size} onChange={(e) => setNewItem({ ...newItem, size: e.target.value })} placeholder="500ml, 10 inch" />
            </div>
            <div>
              <Label>Replacement Cost (₹) *</Label>
              <Input type="number" value={newItem.replacement_cost} onChange={(e) => setNewItem({ ...newItem, replacement_cost: e.target.value })} />
            </div>
            <div>
              <Label>Department *</Label>
              <Select value={newItem.department} onValueChange={(v) => setNewItem({ ...newItem, department: v })}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {constants.departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Expected Life Cycles</Label>
              <Input type="number" value={newItem.expected_life_cycles} onChange={(e) => setNewItem({ ...newItem, expected_life_cycles: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItem(false)}>Cancel</Button>
            <Button onClick={handleCreateItem}>Create Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movement Dialog */}
      <Dialog open={showMovement} onOpenChange={setShowMovement}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Movement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Crockery Item *</Label>
              <Select value={movementForm.crockery_id} onValueChange={(v) => setMovementForm({ ...movementForm, crockery_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select item" /></SelectTrigger>
                <SelectContent>
                  {crockeryItems.map(item => <SelectItem key={item.id} value={item.id}>{item.item_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>From Store *</Label>
                <Select value={movementForm.from_store} onValueChange={(v) => setMovementForm({ ...movementForm, from_store: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {constants.stores.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    <SelectItem value="External">External/Purchase</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>To Store *</Label>
                <Select value={movementForm.to_store} onValueChange={(v) => setMovementForm({ ...movementForm, to_store: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {constants.stores.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    <SelectItem value="Broken">Broken</SelectItem>
                    <SelectItem value="Scrap">Scrap</SelectItem>
                    <SelectItem value="Lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity *</Label>
                <Input type="number" value={movementForm.quantity} onChange={(e) => setMovementForm({ ...movementForm, quantity: e.target.value })} />
              </div>
              <div>
                <Label>Movement Type *</Label>
                <Select value={movementForm.movement_type} onValueChange={(v) => setMovementForm({ ...movementForm, movement_type: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {constants.movement_types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Remarks</Label>
              <Input value={movementForm.remarks} onChange={(e) => setMovementForm({ ...movementForm, remarks: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMovement(false)}>Cancel</Button>
            <Button onClick={handleCreateMovement}>Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Opening Balance Dialog */}
      <Dialog open={showOpeningBalance} onOpenChange={setShowOpeningBalance}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Opening Balance</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Crockery Item *</Label>
              <Select value={openingForm.crockery_id} onValueChange={(v) => setOpeningForm({ ...openingForm, crockery_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select item" /></SelectTrigger>
                <SelectContent>
                  {crockeryItems.map(item => <SelectItem key={item.id} value={item.id}>{item.item_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Store *</Label>
              <Select value={openingForm.store} onValueChange={(v) => setOpeningForm({ ...openingForm, store: v })}>
                <SelectTrigger><SelectValue placeholder="Select store" /></SelectTrigger>
                <SelectContent>
                  {constants.stores.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantity *</Label>
              <Input type="number" value={openingForm.quantity} onChange={(e) => setOpeningForm({ ...openingForm, quantity: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOpeningBalance(false)}>Cancel</Button>
            <Button onClick={handleSetOpeningBalance}>Set Balance</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Explain Variance Dialog */}
      <Dialog open={showExplain} onOpenChange={setShowExplain}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Explain Variance</DialogTitle>
            <DialogDescription>
              {selectedVariance && `${selectedVariance.item_name} - Variance: ${selectedVariance.variance} units (${formatCurrency(selectedVariance.loss_value)})`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Explanation *</Label>
              <Select value={explanationForm.explanation} onValueChange={(v) => setExplanationForm({ ...explanationForm, explanation: v })}>
                <SelectTrigger><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>
                  {constants.explanation_options.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Remarks</Label>
              <Input value={explanationForm.remarks} onChange={(e) => setExplanationForm({ ...explanationForm, remarks: e.target.value })} placeholder="Additional details" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExplain(false)}>Cancel</Button>
            <Button onClick={handleExplainVariance}>Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
